import React, { useCallback, useEffect, useState, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, collectionGroup, query, where, getDocs, doc, getDoc, orderBy, Timestamp } from 'firebase/firestore';
import { CalendarIcon, ClockIcon, VideoIcon, MapPinIcon, LoaderIcon, BookOpenIcon, CheckCircleIcon, ChevronRightIcon } from 'lucide-react';
import { ensureStudentLink } from '../../services/studentService';
import { isLessonOnline } from '../../utils/lessonHelpers';
import { StudentJoinLessonPanel } from '../modals/StudentJoinLessonPanel';
interface UpcomingLesson {
  id: string;
  teacherId: string;
  teacherName: string;
  teacherAvatar: string;
  instrument: string;
  date: string;
  time: string;
  duration: string;
  format: 'Virtual' | 'In-person';
  status: string;
  meetingLink?: string;
  notes?: string;
  taskTitle?: string;
  taskDueDate?: string;
  taskStatus?: string;
  taskNotes?: string;
  isNext: boolean;
  startAt: Date;
  durationMinutes: number;
  location: string;
  rawStatus: string;
}
// Cache keys and TTL
const CACHE_KEY = 'studentDashboard_lessons';
const CACHE_NAME_KEY = 'studentDashboard_name';
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes (reduced from 5)
// NOTE: SELF_LINK_KEY removed — student linking is now self-healing and
// runs unconditionally on every dashboard mount via ensureStudentLink().
// The previous sessionStorage gate permanently blocked retries after the
// first attempt, which caused silent failures when initial linking did
// not actually succeed.
function getCachedData<T>(key: string, uid: string): T | null {
  try {
    const raw = sessionStorage.getItem(`${key}_${uid}`);
    if (!raw) return null;
    const {
      data,
      timestamp
    } = JSON.parse(raw);
    if (Date.now() - timestamp < CACHE_TTL) return data;
    return null;
  } catch {
    return null;
  }
}
function setCachedData<T>(key: string, uid: string, data: T): void {
  try {
    sessionStorage.setItem(`${key}_${uid}`, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}
export const StudentDashboard = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser
  } = useAuth();
  const [studentName, setStudentName] = useState('');
  const [upcomingLessons, setUpcomingLessons] = useState<UpcomingLesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const fetchInProgress = useRef(false);
  // State for Join Lesson Panel
  const [isJoinLessonPanelOpen, setIsJoinLessonPanelOpen] = useState(false);
  const [selectedLessonForJoin, setSelectedLessonForJoin] = useState<UpcomingLesson | null>(null);
  const formatLessons = useCallback((topLessons: any[], teacherNameMap: Record<string, string>, taskTitleMap: Record<string, {
    title: string;
    dueDate?: string;
    status?: string;
    notes?: string;
  }>): UpcomingLesson[] => {
    return topLessons.map((lesson, index) => {
      const startDate = lesson.startAt?.toDate ? lesson.startAt.toDate() : new Date(lesson.startAt);
      const teacherFullName = teacherNameMap[lesson.teacherId] || 'Teacher';
      const initials = teacherFullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);
      let dateStr: string;
      if (startDate.toDateString() === today.toDateString()) {
        dateStr = 'Today';
      } else if (startDate.toDateString() === tomorrow.toDateString()) {
        dateStr = 'Tomorrow';
      } else {
        dateStr = startDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });
      }
      const timeStr = startDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      const durationMin = lesson.durationMinutes || 60;
      const durationStr = `${durationMin} min`;
      const location = (lesson.location || '').toLowerCase();
      const isOnline = location.includes('online') || location.includes('virtual') || location.includes('zoom') || location.includes('meet') || !!lesson.meetingLink;
      const format: 'Virtual' | 'In-person' = isOnline ? 'Virtual' : 'In-person';
      const rawStatus = (lesson.status || 'SCHEDULED').toUpperCase();
      let statusDisplay = 'Scheduled';
      if (rawStatus === 'SCHEDULED') statusDisplay = 'Confirmed';else if (rawStatus === 'COMPLETED') statusDisplay = 'Completed';
      const linkedTask = taskTitleMap[lesson.id];
      return {
        id: lesson.id,
        teacherId: lesson.teacherId,
        teacherName: teacherFullName,
        teacherAvatar: initials,
        instrument: lesson.instrument || 'Music',
        date: dateStr,
        time: timeStr,
        duration: durationStr,
        format,
        status: statusDisplay,
        meetingLink: lesson.meetingLink,
        notes: lesson.notes || '',
        taskTitle: linkedTask?.title || '',
        taskDueDate: linkedTask?.dueDate || '',
        taskStatus: linkedTask?.status || '',
        taskNotes: linkedTask?.notes || '',
        isNext: index === 0,
        startAt: startDate,
        durationMinutes: durationMin,
        location: lesson.location || '',
        rawStatus
      };
    });
  }, []);
  useEffect(() => {
    if (!currentUser?.uid) return;
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;
    const uid = currentUser.uid;
    // Instantly show cached data if available (stale-while-revalidate pattern)
    // Cache has a 2-minute TTL — fresh data is always fetched in background
    const cachedLessons = getCachedData<UpcomingLesson[]>(CACHE_KEY, uid);
    const cachedName = getCachedData<string>(CACHE_NAME_KEY, uid);
    if (cachedLessons && cachedLessons.length > 0) {
      // Rehydrate Date objects lost during JSON serialization
      const rehydrated = cachedLessons.map(l => ({
        ...l,
        startAt: new Date(l.startAt)
      }));
      setUpcomingLessons(rehydrated);
      setIsLoading(false);
    }
    if (cachedName) {
      setStudentName(cachedName);
    }
    const fetchDashboardData = async () => {
      // Only show loading spinner if we have no cached data
      if (!cachedLessons || cachedLessons.length === 0) {
        setIsLoading(true);
      }
      try {
        // Step 1: Get student name (if users/{uid} exists)
        const userDocRef = doc(db, 'users', uid);
        const userDoc = await getDoc(userDocRef);
        let userDataEmail: string | undefined;
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const name = userData.firstName || userData.name || 'Student';
          setStudentName(name);
          setCachedData(CACHE_NAME_KEY, uid, name);
          userDataEmail = userData.email;
        }
        // Self-heal student↔teacher linkage on EVERY mount — runs
        // UNCONDITIONALLY, regardless of whether users/{uid} exists.
        // Falls back to currentUser.email when the users doc is absent
        // (matches the resilient pattern used in StudentSchedule.tsx).
        //
        // ensureStudentLink handles:
        //   - missing linkedUid (initial link)
        //   - stale linkedUid (e.g. after account recreation)
        //   - already-correct linkedUid (no-op)
        // It has its own in-memory dedup, so calling it on every mount
        // is safe and cheap.
        const studentEmail = userDataEmail || currentUser.email || '';
        if (studentEmail) {
          try {
            await ensureStudentLink(studentEmail, uid);
          } catch (e) {
            console.error('[StudentDashboard] ensureStudentLink failed:', e);
          }
        } else {
          console.warn('[StudentDashboard] No email available for ensureStudentLink; skipping self-heal');
        }
        // Step 2: Find teacher-student links
        const studentsQuery = query(collectionGroup(db, 'students'), where('linkedUid', '==', uid));
        const studentsSnap = await getDocs(studentsQuery);
        if (studentsSnap.empty) {
          console.warn('[StudentDashboard] No linked roster docs found for uid:', uid, '— if a teacher has already added this student, the roster doc ' + 'may be missing linkedUid. See [ensureStudentLink] logs above.');
          setUpcomingLessons([]);
          setCachedData(CACHE_KEY, uid, []);
          setIsLoading(false);
          fetchInProgress.current = false;
          return;
        }
        // Build student-teacher pairs
        const studentTeacherPairs: {
          studentId: string;
          teacherId: string;
        }[] = [];
        studentsSnap.docs.forEach(docSnap => {
          const pathSegments = docSnap.ref.path.split('/');
          const teacherId = pathSegments[1];
          const studentData = docSnap.data();
          const studentIdForLessons = studentData.studentId || docSnap.id;
          studentTeacherPairs.push({
            studentId: studentIdForLessons,
            teacherId
          });
        });
        // Map teacherId -> unique studentIds linked to this user under that teacher.
        // Used to scope task queries to only the current student's tasks.
        //
        // IMPORTANT: There are TWO competing identifier conventions in the codebase:
        //   - lesson docs (calendar) store `studentId` = the roster doc's `data.studentId` field
        //   - task docs store `studentId` = the roster doc id itself (auto-generated)
        // To make the task `in` query match either convention, we include BOTH
        // identifier types for each linked roster doc. The filter still only
        // matches docs that belong to this user (no cross-user leak), because
        // both ids are sourced exclusively from THIS user's own roster docs.
        const teacherToStudentIds: Record<string, string[]> = {};
        studentsSnap.docs.forEach(docSnap => {
          const pathSegments = docSnap.ref.path.split('/');
          const teacherId = pathSegments[1];
          const data = docSnap.data();
          if (!teacherToStudentIds[teacherId]) {
            teacherToStudentIds[teacherId] = [];
          }
          // Roster doc id (the convention task docs appear to use)
          if (!teacherToStudentIds[teacherId].includes(docSnap.id)) {
            teacherToStudentIds[teacherId].push(docSnap.id);
          }
          // data.studentId field (the convention lesson docs use; included for
          // robustness in case some tasks were written with this value)
          if (data.studentId && data.studentId !== docSnap.id && !teacherToStudentIds[teacherId].includes(data.studentId)) {
            teacherToStudentIds[teacherId].push(data.studentId);
          }
        });
        // Step 3: Query upcoming lessons (parallel per teacher)
        const now = new Date();
        const lessonPromises = studentTeacherPairs.map(pair => {
          const calendarQuery = query(collectionGroup(db, 'calendar'), where('studentId', '==', pair.studentId), where('startAt', '>=', Timestamp.fromDate(now)), orderBy('startAt', 'asc'));
          return getDocs(calendarQuery).then(snap => {
            const lessons: any[] = [];
            snap.docs.forEach(lessonDoc => {
              const data = lessonDoc.data();
              if (data.status !== 'CANCELED' && data.cancelled !== true) {
                lessons.push({
                  id: lessonDoc.id,
                  teacherId: pair.teacherId,
                  ...data
                });
              }
            });
            return lessons;
          });
        });
        const lessonResults = await Promise.all(lessonPromises);
        const allLessons = lessonResults.flat();
        allLessons.sort((a, b) => {
          const aTime = a.startAt?.toDate ? a.startAt.toDate().getTime() : new Date(a.startAt).getTime();
          const bTime = b.startAt?.toDate ? b.startAt.toDate().getTime() : new Date(b.startAt).getTime();
          return aTime - bTime;
        });
        // Show ALL upcoming lessons (no artificial limit)
        const topLessons = allLessons;
        // Step 4: Resolve teacher names + tasks in parallel
        const teacherIds = [...new Set(topLessons.map(l => l.teacherId))];
        const teacherNameMap: Record<string, string> = {};
        const taskTitleMap: Record<string, {
          title: string;
          dueDate?: string;
          status?: string;
          notes?: string;
        }> = {};
        // Fetch teacher names and tasks in parallel
        await Promise.all(teacherIds.map(async tid => {
          // Teacher name
          const namePromise = getDoc(doc(db, 'publicTeacherProfiles', tid)).then(profileDoc => {
            if (profileDoc.exists()) {
              const p = profileDoc.data();
              teacherNameMap[tid] = p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Teacher';
            } else {
              teacherNameMap[tid] = 'Teacher';
            }
          }).catch(() => {
            teacherNameMap[tid] = 'Teacher';
          });
          // Tasks for this teacher — scoped to ONLY this student's tasks.
          // Previously this fetched ALL tasks for the teacher and filtered
          // client-side, which exposed other students' task data.
          const lessonIdsForTeacher = topLessons.filter(l => l.teacherId === tid).map(l => l.id);
          const studentIdsForTeacher = teacherToStudentIds[tid] || [];
          const taskPromise = lessonIdsForTeacher.length > 0 && studentIdsForTeacher.length > 0 ? getDocs(query(collection(db, 'teachers', tid, 'tasks'), where('studentId', 'in', studentIdsForTeacher))).then(tasksSnap => {
            tasksSnap.forEach(taskDoc => {
              const taskData = taskDoc.data();
              if (taskData.lessonId && lessonIdsForTeacher.includes(taskData.lessonId) && taskData.title) {
                let formattedDueDate = '';
                if (taskData.dueDate) {
                  try {
                    const d = taskData.dueDate?.toDate ? taskData.dueDate.toDate() : new Date(taskData.dueDate);
                    if (!isNaN(d.getTime())) {
                      formattedDueDate = d.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      });
                    }
                  } catch {
                    formattedDueDate = '';
                  }
                }
                taskTitleMap[taskData.lessonId] = {
                  title: taskData.title,
                  dueDate: formattedDueDate,
                  status: taskData.status || '',
                  notes: taskData.notes || ''
                };
              }
            });
          }).catch(() => {}) : Promise.resolve();
          await Promise.all([namePromise, taskPromise]);
        }));
        // Step 5: Format and update
        const formatted = formatLessons(topLessons, teacherNameMap, taskTitleMap);
        setUpcomingLessons(formatted);
        setCachedData(CACHE_KEY, uid, formatted);
      } catch (error) {
        console.error('[StudentDashboard] Error fetching data:', error);
        setUpcomingLessons([]);
      } finally {
        setIsLoading(false);
        fetchInProgress.current = false;
      }
    };
    fetchDashboardData();
  }, [currentUser?.uid, formatLessons]);
  return <div className={`min-h-screen ${isDark ? '' : 'bg-gray-100/60'} p-6 md:p-8`} data-id="element-7196">
      <div className="max-w-4xl space-y-8" data-id="element-7197">
        {/* Welcome Header */}
        <div className={`${isDark ? '' : 'pb-6 border-b border-gray-200/80'}`} data-id="element-7198">
          <h1 className={`text-3xl md:text-4xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7199">
            Welcome back{studentName ? `, ${studentName}` : ''}
          </h1>
          <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7200">
            {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>

        {/* Upcoming Lessons Section */}
        <div data-id="element-7201">
          <div className="flex items-center gap-2.5 mb-5" data-id="element-7202">
            <div className={`p-1.5 rounded-lg ${isDark ? 'bg-navy-800' : 'bg-blue-50'}`} data-id="element-7203">
              <CalendarIcon className={`h-4.5 w-4.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} data-id="element-7204" />
            </div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7205">
              Upcoming Lessons
            </h2>
            {upcomingLessons.length > 0 && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600 ring-1 ring-blue-200/50'}`} data-id="element-7206">
                {upcomingLessons.length}
              </span>}
          </div>

          {isLoading ? <div className="space-y-4" data-id="element-7207">
              {/* Hero skeleton card */}
              <div className={`rounded-xl p-5 ${isDark ? 'bg-navy-800 border border-blue-500/20' : 'bg-white border border-blue-200/60 shadow-md shadow-blue-100/40 ring-1 ring-blue-50'}`} data-id="element-7208">
                <div className="flex items-center gap-4 animate-pulse" data-id="element-7209">
                  <div className={`w-12 h-12 rounded-full flex-shrink-0 ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7210" />
                  <div className="flex-1 min-w-0 space-y-2.5" data-id="element-7211">
                    <div className="flex items-center gap-2" data-id="element-7212">
                      <div className={`h-4 w-32 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7213" />
                      <div className={`h-4 w-16 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7214" />
                    </div>
                    <div className={`h-3 w-24 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7215" />
                    <div className="flex items-center gap-3" data-id="element-7216">
                      <div className={`h-3 w-28 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7217" />
                      <div className={`h-3 w-16 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7218" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Regular skeleton cards */}
              {[1, 2].map(i => <div key={i} className={`rounded-xl p-5 ${isDark ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-100 shadow-sm shadow-gray-100/50'}`} data-id="element-7219">
                  <div className="flex items-center gap-4 animate-pulse" data-id="element-7220">
                    <div className={`w-10 h-10 rounded-full flex-shrink-0 ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7221" />
                    <div className="flex-1 min-w-0 space-y-2" data-id="element-7222">
                      <div className={`h-4 w-28 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-7223" />
                      <div className={`h-3 w-20 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7224" />
                      <div className="flex items-center gap-3" data-id="element-7225">
                        <div className={`h-3 w-24 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7226" />
                        <div className={`h-3 w-14 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7227" />
                      </div>
                    </div>
                  </div>
                </div>)}
            </div> : upcomingLessons.length > 0 ? <div className="space-y-4" data-id="element-7228">
              {/* Hero card — first upcoming lesson */}
              {(() => {
            const lesson = upcomingLessons[0];
            return <div key={lesson.id} className={`rounded-xl p-5 cursor-pointer transition-all duration-200 ease-out ${isDark ? 'bg-navy-800 border border-blue-500/20 shadow-md shadow-blue-500/5 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/15 hover:bg-navy-800/80' : 'bg-white border border-blue-200/60 shadow-md shadow-blue-100/40 ring-1 ring-blue-50 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-100/60'}`} data-id="element-7229">
                    <div className="flex items-center justify-between gap-4" data-id="element-7230">
                      <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-7231">
                        {/* Teacher Avatar */}
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold flex-shrink-0 ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'}`} data-id="element-7232">
                          {lesson.teacherAvatar}
                        </div>

                        {/* Lesson info */}
                        <div className="flex-1 min-w-0" data-id="element-7233">
                          <div className="flex items-center gap-2 mb-1" data-id="element-7234">
                            <h3 className={`text-base font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7235">
                              {lesson.instrument}
                            </h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-700 ring-1 ring-green-200/50'}`} data-id="element-7236">
                              {lesson.status}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${isDark ? 'bg-navy-700 text-gray-300' : 'bg-gray-50 text-gray-500 ring-1 ring-gray-200/50'}`} data-id="element-7237">
                              {lesson.duration}
                            </span>
                          </div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-1`} data-id="element-7238">
                            with {lesson.teacherName}
                          </p>
                          <div className="flex flex-wrap items-center gap-3 text-sm" data-id="element-7239">
                            <div className="flex items-center gap-1.5" data-id="element-7240">
                              <ClockIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7241" />
                              <span className={isDark ? 'text-gray-300' : 'text-gray-600'} data-id="element-7242">
                                {lesson.date} at {lesson.time}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-7243">
                              {lesson.format === 'Virtual' ? <VideoIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7244" /> : <MapPinIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7245" />}
                              <span className={isDark ? 'text-gray-300' : 'text-gray-600'} data-id="element-7246">
                                {lesson.format === 'Virtual' ? 'Online' : lesson.format}
                              </span>
                            </div>
                          </div>
                          {lesson.notes && <p className={`text-xs mt-1.5 truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7247">
                              🎵 {lesson.notes}
                            </p>}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2 flex-shrink-0" data-id="element-7248">
                        {lesson.format === 'Virtual' && lesson.rawStatus !== 'COMPLETED' && lesson.rawStatus !== 'CANCELED' && isLessonOnline(lesson.location) && (() => {
                    const now = new Date();
                    const lessonStart = new Date(lesson.startAt);
                    const lessonEnd = new Date(lessonStart.getTime() + lesson.durationMinutes * 60 * 1000);
                    const windowStart = new Date(lessonStart.getTime() - 60 * 60 * 1000);
                    const windowEnd = new Date(lessonEnd.getTime() + 15 * 60 * 1000);
                    const inWindow = now >= windowStart && now <= windowEnd;
                    return inWindow;
                  })() && <button onClick={e => {
                    e.stopPropagation();
                    setSelectedLessonForJoin(lesson);
                    setIsJoinLessonPanelOpen(true);
                  }} className="px-4 py-2 bg-transparent border-2 border-green-500 text-green-500 hover:bg-green-500/10 rounded-lg text-sm font-medium whitespace-nowrap transition-colors" data-id="element-7249">
                              Join Now
                            </button>}
                      </div>
                    </div>

                    {/* Task section */}
                    {lesson.taskTitle && <div className={`mt-3 mx-5 p-3 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700' : 'bg-gray-50/80 border-gray-100'}`} data-id="element-7250">
                        <div className="flex items-center gap-1.5" data-id="element-7251">
                          <BookOpenIcon className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} data-id="element-7252" />
                          <span className={`text-xs font-semibold ${isDark ? 'text-blue-300' : 'text-blue-600'}`} data-id="element-7253">
                            Task:
                          </span>
                          <span className={`text-xs font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`} data-id="element-7254">
                            {lesson.taskTitle}
                          </span>
                          {lesson.taskStatus && <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 capitalize ${lesson.taskStatus.toLowerCase() === 'completed' ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-700' : lesson.taskStatus.toLowerCase() === 'in progress' || lesson.taskStatus.toLowerCase() === 'in-progress' ? isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-50 text-yellow-700' : isDark ? 'bg-gray-600/40 text-gray-400' : 'bg-gray-100 text-gray-500'}`} data-id="element-7255">
                              {lesson.taskStatus}
                            </span>}
                        </div>
                        {(lesson.taskDueDate || lesson.taskNotes) && <div className="flex flex-col gap-1 mt-1.5 ml-5" data-id="element-7256">
                            {lesson.taskDueDate && <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7257">
                                <span className="font-medium" data-id="element-7258">Due:</span>{' '}
                                {lesson.taskDueDate}
                              </span>}
                            {lesson.taskNotes && <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-7259">
                                <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7260">
                                  Notes:
                                </span>{' '}
                                {lesson.taskNotes}
                              </p>}
                          </div>}
                      </div>}
                  </div>;
          })()}

              {/* Additional upcoming lessons */}
              {upcomingLessons.length > 1 && <div className="space-y-3" data-id="element-7261">
                  {upcomingLessons.slice(1).map(lesson => <div key={lesson.id} className={`rounded-xl p-4 cursor-pointer transition-all duration-200 ease-out ${isDark ? 'bg-navy-800 border border-navy-700 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/5 hover:bg-navy-800/80' : 'bg-white border border-gray-100 shadow-sm shadow-gray-100/50 hover:-translate-y-0.5 hover:shadow-md hover:shadow-gray-200/60'}`} data-id="element-7262">
                      <div className="flex items-center gap-4" data-id="element-7263">
                        {/* Teacher Avatar */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${isDark ? 'bg-navy-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-7264">
                          {lesson.teacherAvatar}
                        </div>

                        {/* Lesson info */}
                        <div className="flex-1 min-w-0" data-id="element-7265">
                          <div className="flex items-center gap-2" data-id="element-7266">
                            <h3 className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7267">
                              {lesson.instrument}
                            </h3>
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${isDark ? 'bg-navy-700 text-gray-300' : 'bg-gray-50 text-gray-500 ring-1 ring-gray-200/50'}`} data-id="element-7268">
                              {lesson.duration}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs" data-id="element-7269">
                            <span className={`flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7270">
                              <ClockIcon className="h-3.5 w-3.5" data-id="element-7271" />
                              {lesson.date} at {lesson.time}
                            </span>
                            <span className={`flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7272">
                              {lesson.format === 'Virtual' ? <VideoIcon className="h-3.5 w-3.5" data-id="element-7273" /> : <MapPinIcon className="h-3.5 w-3.5" data-id="element-7274" />}
                              {lesson.format === 'Virtual' ? 'Online' : lesson.format}
                            </span>
                            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7275">
                              with {lesson.teacherName}
                            </span>
                          </div>
                        </div>

                        {/* Join button if applicable */}
                        <div className="flex-shrink-0" data-id="element-7276">
                          {lesson.format === 'Virtual' && lesson.rawStatus !== 'COMPLETED' && lesson.rawStatus !== 'CANCELED' && isLessonOnline(lesson.location) && (() => {
                    const now = new Date();
                    const lessonStart = new Date(lesson.startAt);
                    const lessonEnd = new Date(lessonStart.getTime() + lesson.durationMinutes * 60 * 1000);
                    const windowStart = new Date(lessonStart.getTime() - 60 * 60 * 1000);
                    const windowEnd = new Date(lessonEnd.getTime() + 15 * 60 * 1000);
                    const inWindow = now >= windowStart && now <= windowEnd;
                    return inWindow;
                  })() && <button onClick={e => {
                    e.stopPropagation();
                    setSelectedLessonForJoin(lesson);
                    setIsJoinLessonPanelOpen(true);
                  }} className="px-3 py-1.5 bg-transparent border-2 border-green-500 text-green-500 hover:bg-green-500/10 rounded-lg text-xs font-medium whitespace-nowrap transition-colors" data-id="element-7277">
                                Join Now
                              </button>}
                        </div>
                      </div>
                    </div>)}
                </div>}

              {/* View My Schedule CTA */}
              <button onClick={() => navigate('/student-schedule')} className={`flex items-center gap-2 text-sm font-medium transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`} data-id="element-7278">
                View My Schedule
                <ChevronRightIcon className="h-4 w-4" data-id="element-7279" />
              </button>
            </div> :
        // Empty state
        <div className={`rounded-xl p-12 text-center ${isDark ? 'bg-navy-800/50 border-2 border-dashed border-navy-700' : 'bg-white border-2 border-dashed border-gray-200 shadow-sm'}`} data-id="element-7280">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7281">
                <CalendarIcon className={`h-8 w-8 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7282" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7283">
                No upcoming lessons yet
              </h3>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7284">
                Your upcoming lessons will appear here.
              </p>
            </div>}
        </div>
      </div>

      {/* Student Join Lesson Panel */}
      <StudentJoinLessonPanel isOpen={isJoinLessonPanelOpen} onClose={() => {
      setIsJoinLessonPanelOpen(false);
      setSelectedLessonForJoin(null);
    }} lesson={selectedLessonForJoin ? {
      id: selectedLessonForJoin.id,
      teacherName: selectedLessonForJoin.teacherName,
      meetingLink: selectedLessonForJoin.meetingLink
    } : null} darkMode={isDark} data-id="element-7285" />
    </div>;
};